import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../store/hooks';
import {
  stockPortfolioService,
  StockAccountOption,
  StockSummary,
  StockPosition,
  StockHistoricalOrder,
  StockPendingOrder,
  LocalStockOrder,
  StockInstrument,
} from '../services/stockPortfolioService';
import StockOrderModal from '../components/admin/StockOrderModal';
import StockInstrumentDrawer from '../components/admin/StockInstrumentDrawer';
import StockGrowthChallengeCard from '../components/stock/StockGrowthChallengeCard';
import StockActivityBotPanel from '../components/stock/StockActivityBotPanel';

/** Set by Market Intelligence "T212 Trade" before navigating to this tab. */
const PENDING_T212_SYMBOL_KEY = 'stockPortfolio.pendingT212Symbol';

/** Map a board symbol (AAPL / SPY) to a Trading 212 instrument ticker — prefer plain US equity. */
function resolveT212Ticker(symbol: string, instruments: StockInstrument[]): string | null {
  const upper = symbol.trim().toUpperCase();
  if (!upper) return null;
  const preferred = `${upper}_US_EQ`;
  const exactPreferred = instruments.find(i => i.ticker.toUpperCase() === preferred);
  if (exactPreferred) return exactPreferred.ticker;

  const isLeveraged = (i: StockInstrument) => {
    const name = `${i.name || ''} ${i.shortName || ''}`.toLowerCase();
    const short = i.ticker.split('_')[0];
    return (
      name.includes('leverage') ||
      name.includes('2x') ||
      name.includes('3x') ||
      name.includes('-1x') ||
      name.includes('short ') ||
      /^\d/.test(short)
    );
  };

  const byShort = instruments.filter(i => i.ticker.split('_')[0].toUpperCase() === upper);
  const plainShort = byShort.find(i => !isLeveraged(i));
  if (plainShort) return plainShort.ticker;

  const starts = instruments.filter(i => i.ticker.toUpperCase().startsWith(`${upper}_`));
  const plainStarts = starts.find(i => !isLeveraged(i) && i.ticker.toUpperCase().includes('_US_'));
  if (plainStarts) return plainStarts.ticker;
  const anyPlain = starts.find(i => !isLeveraged(i));
  if (anyPlain) return anyPlain.ticker;
  return byShort[0]?.ticker || starts[0]?.ticker || null;
}

const POLL_MS = 60_000;

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
};

function fmtMoney(n: number | null | undefined, currency: string): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(v);
  } catch {
    return `${v < 0 ? '-' : ''}${currency} ${Math.abs(v).toFixed(2)}`;
  }
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/** T212 tickers look like "AAPL_US_EQ" — show the human part. */
function displayTicker(ticker: string | null | undefined): string {
  if (!ticker) return '—';
  return ticker.split('_')[0];
}

const pnlColor = (v: number | null | undefined) =>
  Number(v ?? 0) >= 0 ? '#15803d' : '#b91c1c';

const StockPortfolio: React.FC = () => {
  const adminPermissions = useAppSelector(state => state.auth.adminUser?.permissions || []);
  const canManage = adminPermissions.includes('MANAGE_STOCK_PORTFOLIO');

  // Stack tables into cards below ~640px so the page stays readable on phones.
  const [isNarrow, setIsNarrow] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false),
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [accounts, setAccounts] = useState<StockAccountOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [challengeBotIds, setChallengeBotIds] = useState<number[]>([]);
  const [summary, setSummary] = useState<StockSummary | null>(null);
  const [positions, setPositions] = useState<StockPosition[]>([]);
  const [orders, setOrders] = useState<StockHistoricalOrder[]>([]);
  const [pendingOrders, setPendingOrders] = useState<StockPendingOrder[]>([]);
  const [localOrders, setLocalOrders] = useState<LocalStockOrder[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [orderModal, setOrderModal] = useState<{ side: 'BUY' | 'SELL'; ticker: string } | null>(null);

  // Browse stocks: T212 instrument catalog, loaded lazily on first search use.
  const [instruments, setInstruments] = useState<StockInstrument[]>([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);
  const [instrumentsLoaded, setInstrumentsLoaded] = useState(false);
  const [browseQuery, setBrowseQuery] = useState('');
  const [drawerTicker, setDrawerTicker] = useState<string | null>(null);
  /** Symbol already opened from Market Intelligence this visit. */
  const pendingSymbolHandled = useRef<string | null>(null);
  const pendingSymbolInFlight = useRef(false);

  const currency = summary?.currencyCode || 'USD';

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const list = await stockPortfolioService.listStockAccounts();
      setAccounts(list);
      setSelectedId(prev => prev ?? (list.length > 0 ? list[0].id : null));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load Trading 212 accounts');
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const loadPortfolio = useCallback(async (configId: number) => {
    setLoadingData(true);
    setError(null);
    try {
      const [sum, pos, hist, pending, local] = await Promise.all([
        stockPortfolioService.getStockSummary(configId),
        stockPortfolioService.getStockPositions(configId),
        stockPortfolioService.getStockOrders(configId),
        stockPortfolioService.getPendingOrders(configId).catch(() => [] as StockPendingOrder[]),
        stockPortfolioService.getLocalOrders(configId).catch(() => [] as LocalStockOrder[]),
      ]);
      setSummary(sum);
      setPositions(pos);
      setOrders(hist);
      setPendingOrders(pending);
      setLocalOrders(local);
    } catch (e: any) {
      setError(e?.message || 'Failed to load portfolio');
    } finally {
      setLoadingData(false);
    }
  }, []);

  const ensureInstrumentsLoaded = useCallback(async () => {
    if (instrumentsLoaded || instrumentsLoading || selectedId == null) return;
    setInstrumentsLoading(true);
    try {
      const list = await stockPortfolioService.getInstruments(selectedId);
      setInstruments(list);
      setInstrumentsLoaded(true);
    } catch {
      // Search stays empty; retried on next focus.
    } finally {
      setInstrumentsLoading(false);
    }
  }, [instrumentsLoaded, instrumentsLoading, selectedId]);

  // Switching accounts invalidates the loaded catalog (DEMO vs LIVE differ).
  // Keep drawer open when selectedId is first set (null → id) so the MI handoff
  // isn't wiped before the pending-symbol effect can open it.
  const prevSelectedIdRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevSelectedIdRef.current;
    prevSelectedIdRef.current = selectedId;
    if (selectedId == null) return;
    setInstruments([]);
    setInstrumentsLoaded(false);
    // Only clear drawer when switching between two real accounts — not on first select.
    if (prev != null && prev !== selectedId) {
      setBrowseQuery('');
      setDrawerTicker(null);
    }
  }, [selectedId]);

  const browseResults = useMemo(() => {
    const q = browseQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    const score = (i: StockInstrument) => {
      const short = i.ticker.split('_')[0].toLowerCase();
      const name = `${i.name || ''} ${i.shortName || ''}`.toLowerCase();
      let s = 0;
      if (short === q) s += 100;
      if (i.ticker.toUpperCase() === `${q.toUpperCase()}_US_EQ`) s += 90;
      if (i.ticker.toUpperCase().endsWith('_US_EQ')) s += 40;
      if (short.startsWith(q)) s += 20;
      if (name.includes('leverage') || name.includes('2x') || name.includes('3x') || /^\d/.test(short)) {
        s -= 80;
      }
      return s;
    };
    return instruments
      .filter(
        i =>
          i.ticker.toLowerCase().includes(q) ||
          (i.name || '').toLowerCase().includes(q) ||
          (i.shortName || '').toLowerCase().includes(q),
      )
      .sort((a, b) => score(b) - score(a))
      .slice(0, 30);
  }, [instruments, browseQuery]);

  const drawerInstrument = useMemo(
    () => (drawerTicker ? instruments.find(i => i.ticker === drawerTicker) || null : null),
    [drawerTicker, instruments],
  );
  const drawerPosition = useMemo(
    () => (drawerTicker ? positions.find(p => p.ticker === drawerTicker) || null : null),
    [drawerTicker, positions],
  );

  const cancelPendingOrder = useCallback(async (orderId: number) => {
    if (selectedId == null) return;
    setCancellingId(orderId);
    try {
      await stockPortfolioService.cancelStockOrder(selectedId, orderId);
      await loadPortfolio(selectedId);
    } catch (e: any) {
      setError(e?.message || 'Failed to cancel order');
    } finally {
      setCancellingId(null);
    }
  }, [selectedId, loadPortfolio]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (selectedId == null) return;
    void loadPortfolio(selectedId);
    const timer = window.setInterval(() => void loadPortfolio(selectedId), POLL_MS);
    return () => window.clearInterval(timer);
  }, [selectedId, loadPortfolio]);

  // Market Intelligence → Stock Portfolio: open the trade drawer for the handed-off symbol.
  useEffect(() => {
    if (selectedId == null || loadingAccounts) return;

    let symbol = '';
    try {
      symbol = (sessionStorage.getItem(PENDING_T212_SYMBOL_KEY) || '').trim().toUpperCase();
    } catch {
      symbol = '';
    }
    // Backward-compatible: older links may still use ?t212Symbol=
    if (!symbol) {
      try {
        symbol = (new URLSearchParams(window.location.search).get('t212Symbol') || '')
          .trim()
          .toUpperCase();
      } catch {
        symbol = '';
      }
    }
    if (!symbol) return;
    if (pendingSymbolHandled.current === symbol || pendingSymbolInFlight.current) return;
    pendingSymbolInFlight.current = true;

    let cancelled = false;
    (async () => {
      try {
        setBrowseQuery(symbol);
        setInstrumentsLoading(true);
        const catalog = await stockPortfolioService.getInstruments(selectedId);
        if (cancelled) return;
        setInstruments(catalog);
        setInstrumentsLoaded(true);
        const resolved = resolveT212Ticker(symbol, catalog);
        if (!resolved) {
          setError(
            `Could not find ${symbol} in your Trading 212 instrument list. Search for it under Find stocks.`,
          );
        } else {
          setDrawerTicker(resolved);
        }
        // Only consume the handoff after a successful attempt so StrictMode remounts can retry.
        pendingSymbolHandled.current = symbol;
        try {
          sessionStorage.removeItem(PENDING_T212_SYMBOL_KEY);
        } catch {
          // ignore
        }
        // Strip legacy query param without React Router (avoids admin tab remount).
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.has('t212Symbol')) {
            url.searchParams.delete('t212Symbol');
            window.history.replaceState(window.history.state, '', url.toString());
          }
        } catch {
          // ignore
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || `Failed to open ${symbol} on Trading 212`);
        }
      } finally {
        pendingSymbolInFlight.current = false;
        if (!cancelled) setInstrumentsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      pendingSymbolInFlight.current = false;
    };
  }, [selectedId, loadingAccounts]);

  const totals = useMemo(() => {
    const cash = summary?.cash;
    return {
      free: cash?.free ?? 0,
      invested: cash?.invested ?? 0,
      total: cash?.total ?? 0,
      unrealized: cash?.ppl ?? 0,
      realized: cash?.result ?? 0,
    };
  }, [summary]);

  const selectedAccount = accounts.find(a => a.id === selectedId) || null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        boxSizing: 'border-box',
        paddingBottom: isNarrow ? 8 : 0,
      }}
    >
      {/* Header */}
      <div style={{ ...cardStyle, padding: isNarrow ? 12 : 16 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: isNarrow ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: isNarrow ? 'stretch' : 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: isNarrow ? 16 : 17, color: '#1e293b' }}>Stock Portfolio</span>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Trading 212</span>
              {selectedAccount && (
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: selectedAccount.environment === 'LIVE' ? '#dcfce7' : '#fef3c7',
                    color: selectedAccount.environment === 'LIVE' ? '#15803d' : '#b45309',
                  }}
                >
                  {selectedAccount.environment === 'LIVE' ? 'LIVE' : 'DEMO / practice'}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
              Real stocks &amp; ETFs held at Trading 212{canManage ? '' : ' — read-only view'}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'stretch',
              flexWrap: 'wrap',
              width: isNarrow ? '100%' : 'auto',
            }}
          >
            {canManage && accounts.length > 0 && selectedId != null && (
              <button
                type="button"
                onClick={() => setOrderModal({ side: 'BUY', ticker: '' })}
                style={{
                  padding: '7px 16px',
                  borderRadius: 999,
                  border: 'none',
                  background: '#15803d',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                  flex: isNarrow ? '1 1 auto' : undefined,
                }}
              >
                + New order
              </button>
            )}
            {accounts.length > 0 && (
              <select
                value={selectedId ?? ''}
                onChange={e => setSelectedId(Number(e.target.value))}
                style={{
                  padding: '7px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: 13,
                  color: '#334155',
                  background: '#fff',
                  flex: isNarrow ? '1 1 100%' : undefined,
                  width: isNarrow ? '100%' : undefined,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.environment})
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => selectedId != null && void loadPortfolio(selectedId)}
              disabled={loadingData || selectedId == null}
              style={{
                padding: '7px 14px',
                borderRadius: 999,
                border: '1px solid #cbd5e1',
                background: '#fff',
                color: '#334155',
                fontSize: 12,
                fontWeight: 700,
                cursor: loadingData ? 'wait' : 'pointer',
                opacity: loadingData ? 0.7 : 1,
                flex: isNarrow ? '1 1 auto' : undefined,
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* No accounts connected */}
      {!loadingAccounts && accounts.length === 0 && !error && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>No Trading 212 account connected</div>
          <p style={{ margin: '8px auto 0', fontSize: 13, color: '#64748b', maxWidth: 480 }}>
            Connect one under <strong>My Trading Accounts</strong> → provider <strong>Trading 212</strong>.
            Generate the API key in the Trading 212 app under Settings → API (Beta) — start with a
            demo/practice account.
          </p>
        </div>
      )}

      {loadingAccounts && (
        <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Loading accounts…</p>
      )}

      {accounts.length > 0 && (
        <>
          {/* Summary cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isNarrow
                ? 'repeat(2, minmax(0, 1fr))'
                : 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 10,
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {[
              { label: 'Free cash', value: fmtMoney(totals.free, currency), color: '#1e293b' },
              { label: 'Invested', value: fmtMoney(totals.invested, currency), color: '#1e293b' },
              { label: 'Unrealized P&L', value: fmtMoney(totals.unrealized, currency), color: pnlColor(totals.unrealized) },
              { label: 'Realized result', value: fmtMoney(totals.realized, currency), color: pnlColor(totals.realized) },
              { label: 'Account total', value: fmtMoney(totals.total, currency), color: '#1e293b' },
            ].map(item => (
              <div key={item.label} style={{ ...cardStyle, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{item.label}</div>
                <div style={{ fontSize: 17, fontWeight: 800, marginTop: 4, color: item.color }}>
                  {loadingData && !summary ? '…' : item.value}
                </div>
              </div>
            ))}
          </div>

          <StockGrowthChallengeCard
            canManage={canManage}
            cardStyle={cardStyle}
            selectedAccountId={selectedId}
            onChallengeBotIdsChange={setChallengeBotIds}
          />

          <StockActivityBotPanel
            canManage={canManage}
            cardStyle={cardStyle}
            selectedAccountId={selectedId}
            challengeBotIds={challengeBotIds}
            onPortfolioRefresh={() => selectedId != null && void loadPortfolio(selectedId)}
          />

          {/* Browse stocks */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 2 }}>Find stocks</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
              Search the full Trading 212 catalog — click a result for details{canManage ? ' and trading' : ''}.
            </div>
            <input
              type="text"
              value={browseQuery}
              onFocus={() => void ensureInstrumentsLoaded()}
              onChange={e => {
                setBrowseQuery(e.target.value);
                void ensureInstrumentsLoaded();
              }}
              placeholder={instrumentsLoading ? 'Loading instrument catalog…' : 'Search by ticker or company name (e.g. AAPL, Tesla)'}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                fontSize: 13,
                color: '#1e293b',
                boxSizing: 'border-box',
              }}
            />
            {browseQuery.trim().length >= 2 && instrumentsLoaded && (
              browseResults.length === 0 ? (
                <p style={{ margin: '10px 0 0', fontSize: 13, color: '#64748b' }}>
                  No instruments match "{browseQuery.trim()}".
                </p>
              ) : (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 8, maxHeight: 280, overflowY: 'auto' }}>
                  {browseResults.map(i => {
                    const heldQty = positions.find(p => p.ticker === i.ticker)?.quantity;
                    return (
                      <button
                        key={i.ticker}
                        type="button"
                        onClick={() => setDrawerTicker(i.ticker)}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '9px 12px',
                          border: 'none',
                          borderBottom: '1px solid #f1f5f9',
                          background: '#fff',
                          cursor: 'pointer',
                          fontSize: 13,
                          textAlign: 'left',
                        }}
                      >
                        <span style={{ fontWeight: 700, color: '#1e293b', flexShrink: 0 }}>{displayTicker(i.ticker)}</span>
                        <span style={{ color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {i.name || i.shortName || ''}
                        </span>
                        {heldQty != null && (
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 700,
                              background: '#dcfce7',
                              color: '#15803d',
                              flexShrink: 0,
                            }}
                          >
                            Holding {heldQty}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )
            )}
          </div>

          {/* Positions */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 10 }}>
              Open positions {positions.length > 0 ? `(${positions.length})` : ''}
            </div>
            {loadingData && positions.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Loading positions…</p>
            ) : positions.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>No open positions on this account.</p>
            ) : isNarrow ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {positions.map(p => {
                  const value = Number(p.quantity ?? 0) * Number(p.currentPrice ?? 0);
                  return (
                    <div
                      key={p.ticker}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setDrawerTicker(p.ticker);
                        void ensureInstrumentsLoaded();
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDrawerTicker(p.ticker);
                          void ensureInstrumentsLoaded();
                        }
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        border: '1px solid #e2e8f0',
                        borderRadius: 10,
                        padding: 12,
                        background: '#fff',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, color: '#086afb', fontSize: 14 }}>{displayTicker(p.ticker)}</span>
                        <span style={{ fontWeight: 800, color: pnlColor(p.ppl), fontSize: 13 }}>
                          {Number(p.ppl ?? 0) >= 0 ? '+' : ''}{fmtMoney(p.ppl, currency)}
                        </span>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <span>Qty {Number(p.quantity ?? 0)}</span>
                        <span style={{ textAlign: 'right' }}>Value {fmtMoney(value, currency)}</span>
                        <span>Avg {fmtMoney(p.averagePrice, currency)}</span>
                        <span style={{ textAlign: 'right' }}>Now {fmtMoney(p.currentPrice, currency)}</span>
                      </div>
                      {canManage && (
                        <div style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setDrawerTicker(p.ticker);
                              void ensureInstrumentsLoaded();
                            }}
                            style={{
                              padding: '4px 12px',
                              borderRadius: 999,
                              border: '1px solid #fecaca',
                              background: '#fff',
                              color: '#b91c1c',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            Sell
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
                <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>
                      <th style={{ padding: '6px 8px' }}>Instrument</th>
                      <th style={{ padding: '6px 8px' }}>Quantity</th>
                      <th style={{ padding: '6px 8px' }}>Avg price</th>
                      <th style={{ padding: '6px 8px' }}>Current</th>
                      <th style={{ padding: '6px 8px' }}>Value</th>
                      <th style={{ padding: '6px 8px' }}>Unrealized P&L</th>
                      <th style={{ padding: '6px 8px' }}>Since</th>
                      {canManage && <th style={{ padding: '6px 8px' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map(p => {
                      const value = Number(p.quantity ?? 0) * Number(p.currentPrice ?? 0);
                      return (
                        <tr
                          key={p.ticker}
                          onClick={() => {
                            setDrawerTicker(p.ticker);
                            void ensureInstrumentsLoaded();
                          }}
                          style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer' }}
                        >
                          <td style={{ padding: '8px', fontWeight: 700, color: '#086afb' }}>{displayTicker(p.ticker)}</td>
                          <td style={{ padding: '8px', color: '#334155' }}>{Number(p.quantity ?? 0)}</td>
                          <td style={{ padding: '8px', color: '#334155' }}>{fmtMoney(p.averagePrice, currency)}</td>
                          <td style={{ padding: '8px', color: '#334155' }}>{fmtMoney(p.currentPrice, currency)}</td>
                          <td style={{ padding: '8px', color: '#334155' }}>{fmtMoney(value, currency)}</td>
                          <td style={{ padding: '8px', fontWeight: 700, color: pnlColor(p.ppl) }}>
                            {Number(p.ppl ?? 0) >= 0 ? '+' : ''}{fmtMoney(p.ppl, currency)}
                          </td>
                          <td style={{ padding: '8px', color: '#64748b' }}>{fmtDate(p.initialFillDate)}</td>
                          {canManage && (
                            <td style={{ padding: '8px' }}>
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setDrawerTicker(p.ticker);
                                  void ensureInstrumentsLoaded();
                                }}
                                style={{
                                  padding: '4px 12px',
                                  borderRadius: 999,
                                  border: '1px solid #fecaca',
                                  background: '#fff',
                                  color: '#b91c1c',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                              >
                                Sell
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pending orders */}
          {(pendingOrders.length > 0 || canManage) && (
            <div style={cardStyle}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 10 }}>
                Pending orders {pendingOrders.length > 0 ? `(${pendingOrders.length})` : ''}
              </div>
              {pendingOrders.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                  No pending orders — limit and stop orders wait here until they fill.
                </p>
              ) : isNarrow ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {pendingOrders.map(o => {
                    const q = Number(o.quantity ?? 0);
                    const isBuy = q >= 0;
                    return (
                      <div
                        key={o.id}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: 10,
                          padding: 12,
                          background: '#fff',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontWeight: 800, color: '#1e293b' }}>{displayTicker(o.ticker)}</span>
                          <span style={{ fontWeight: 800, color: isBuy ? '#15803d' : '#b91c1c', fontSize: 12 }}>
                            {isBuy ? 'BUY' : 'SELL'}
                          </span>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                          <span>{o.type || '—'}</span>
                          <span style={{ textAlign: 'right' }}>Qty {Math.abs(q) || '—'}</span>
                          <span>
                            {o.limitPrice != null
                              ? fmtMoney(o.limitPrice, currency)
                              : o.stopPrice != null
                                ? fmtMoney(o.stopPrice, currency)
                                : '—'}
                          </span>
                          <span style={{ textAlign: 'right' }}>{fmtDate(o.creationTime)}</span>
                        </div>
                        {canManage && (
                          <button
                            type="button"
                            onClick={() => void cancelPendingOrder(o.id)}
                            disabled={cancellingId === o.id}
                            style={{
                              marginTop: 10,
                              padding: '6px 12px',
                              borderRadius: 999,
                              border: '1px solid #cbd5e1',
                              background: '#fff',
                              color: '#334155',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: cancellingId === o.id ? 'wait' : 'pointer',
                              opacity: cancellingId === o.id ? 0.6 : 1,
                            }}
                          >
                            {cancellingId === o.id ? 'Cancelling…' : 'Cancel'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
                  <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>
                        <th style={{ padding: '6px 8px' }}>Instrument</th>
                        <th style={{ padding: '6px 8px' }}>Side</th>
                        <th style={{ padding: '6px 8px' }}>Type</th>
                        <th style={{ padding: '6px 8px' }}>Quantity</th>
                        <th style={{ padding: '6px 8px' }}>Limit / Stop</th>
                        <th style={{ padding: '6px 8px' }}>Placed</th>
                        {canManage && <th style={{ padding: '6px 8px' }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingOrders.map(o => {
                        const q = Number(o.quantity ?? 0);
                        const isBuy = q >= 0;
                        return (
                          <tr key={o.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px', fontWeight: 700, color: '#1e293b' }}>{displayTicker(o.ticker)}</td>
                            <td style={{ padding: '8px', fontWeight: 700, color: isBuy ? '#15803d' : '#b91c1c' }}>
                              {isBuy ? 'BUY' : 'SELL'}
                            </td>
                            <td style={{ padding: '8px', color: '#334155' }}>{o.type || '—'}</td>
                            <td style={{ padding: '8px', color: '#334155' }}>{Math.abs(q) || '—'}</td>
                            <td style={{ padding: '8px', color: '#334155' }}>
                              {o.limitPrice != null
                                ? fmtMoney(o.limitPrice, currency)
                                : o.stopPrice != null
                                  ? fmtMoney(o.stopPrice, currency)
                                  : '—'}
                            </td>
                            <td style={{ padding: '8px', color: '#64748b' }}>{fmtDate(o.creationTime)}</td>
                            {canManage && (
                              <td style={{ padding: '8px' }}>
                                <button
                                  type="button"
                                  onClick={() => void cancelPendingOrder(o.id)}
                                  disabled={cancellingId === o.id}
                                  style={{
                                    padding: '4px 12px',
                                    borderRadius: 999,
                                    border: '1px solid #cbd5e1',
                                    background: '#fff',
                                    color: '#334155',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: cancellingId === o.id ? 'wait' : 'pointer',
                                    opacity: cancellingId === o.id ? 0.6 : 1,
                                  }}
                                >
                                  {cancellingId === o.id ? 'Cancelling…' : 'Cancel'}
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Order history */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 10 }}>Recent orders</div>
            {loadingData && orders.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Loading orders…</p>
            ) : orders.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>No order history yet.</p>
            ) : isNarrow ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {orders.slice(0, 25).map((o, idx) => (
                  <div
                    key={o.id ?? o.fillId ?? idx}
                    style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#fff' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontWeight: 800, color: '#1e293b' }}>{displayTicker(o.ticker)}</span>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 700,
                          background: o.status === 'FILLED' ? '#dcfce7' : '#f1f5f9',
                          color: o.status === 'FILLED' ? '#15803d' : '#64748b',
                        }}
                      >
                        {o.status || '—'}
                      </span>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <span>{o.type || '—'}</span>
                      <span style={{ textAlign: 'right' }}>Qty {o.filledQuantity ?? o.orderedQuantity ?? '—'}</span>
                      <span>{fmtMoney(o.fillPrice, currency)}</span>
                      <span style={{ textAlign: 'right' }}>{fmtDate(o.dateExecuted || o.dateCreated)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
                <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>
                      <th style={{ padding: '6px 8px' }}>Instrument</th>
                      <th style={{ padding: '6px 8px' }}>Type</th>
                      <th style={{ padding: '6px 8px' }}>Quantity</th>
                      <th style={{ padding: '6px 8px' }}>Fill price</th>
                      <th style={{ padding: '6px 8px' }}>Status</th>
                      <th style={{ padding: '6px 8px' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 25).map((o, idx) => (
                      <tr key={o.id ?? o.fillId ?? idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px', fontWeight: 700, color: '#1e293b' }}>{displayTicker(o.ticker)}</td>
                        <td style={{ padding: '8px', color: '#334155' }}>{o.type || '—'}</td>
                        <td style={{ padding: '8px', color: '#334155' }}>{o.filledQuantity ?? o.orderedQuantity ?? '—'}</td>
                        <td style={{ padding: '8px', color: '#334155' }}>{fmtMoney(o.fillPrice, currency)}</td>
                        <td style={{ padding: '8px' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 700,
                              background: o.status === 'FILLED' ? '#dcfce7' : '#f1f5f9',
                              color: o.status === 'FILLED' ? '#15803d' : '#64748b',
                            }}
                          >
                            {o.status || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '8px', color: '#64748b' }}>{fmtDate(o.dateExecuted || o.dateCreated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Platform order audit trail */}
          {localOrders.length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 2 }}>
                Placed via this platform ({localOrders.length})
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
                Audit trail of orders sent from here — orders placed directly in the Trading 212 app
                appear only under Recent orders above.
              </div>
              {isNarrow ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {localOrders.slice(0, 25).map(o => (
                    <div
                      key={o.id}
                      style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#fff' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontWeight: 800, color: '#1e293b' }}>{displayTicker(o.ticker)}</span>
                        <span style={{ fontWeight: 800, color: o.side === 'BUY' ? '#15803d' : '#b91c1c', fontSize: 12 }}>
                          {o.side}
                        </span>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <span>
                          {o.orderType}
                          {o.limitPrice != null ? ` @ ${fmtMoney(o.limitPrice, currency)}` : ''}
                          {o.stopPrice != null ? ` @ ${fmtMoney(o.stopPrice, currency)}` : ''}
                        </span>
                        <span style={{ textAlign: 'right' }}>Qty {o.quantity}</span>
                        <span>
                          <span
                            title={o.errorMessage || undefined}
                            style={{
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 700,
                              background:
                                o.status === 'FILLED' ? '#dcfce7'
                                : o.status === 'REJECTED' ? '#fee2e2'
                                : '#f1f5f9',
                              color:
                                o.status === 'FILLED' ? '#15803d'
                                : o.status === 'REJECTED' ? '#b91c1c'
                                : '#64748b',
                            }}
                          >
                            {o.status}
                          </span>
                        </span>
                        <span style={{ textAlign: 'right' }}>{fmtDate(o.createdAt)}</span>
                      </div>
                      {o.status === 'REJECTED' && o.errorMessage && (
                        <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 8, wordBreak: 'break-word' }}>
                          {o.errorMessage}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
                  <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>
                        <th style={{ padding: '6px 8px' }}>Instrument</th>
                        <th style={{ padding: '6px 8px' }}>Side</th>
                        <th style={{ padding: '6px 8px' }}>Type</th>
                        <th style={{ padding: '6px 8px' }}>Quantity</th>
                        <th style={{ padding: '6px 8px' }}>Status</th>
                        <th style={{ padding: '6px 8px' }}>Placed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localOrders.slice(0, 25).map(o => (
                        <tr key={o.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px', fontWeight: 700, color: '#1e293b' }}>{displayTicker(o.ticker)}</td>
                          <td style={{ padding: '8px', fontWeight: 700, color: o.side === 'BUY' ? '#15803d' : '#b91c1c' }}>
                            {o.side}
                          </td>
                          <td style={{ padding: '8px', color: '#334155' }}>
                            {o.orderType}
                            {o.limitPrice != null ? ` @ ${fmtMoney(o.limitPrice, currency)}` : ''}
                            {o.stopPrice != null ? ` @ ${fmtMoney(o.stopPrice, currency)}` : ''}
                          </td>
                          <td style={{ padding: '8px', color: '#334155' }}>{o.quantity}</td>
                          <td style={{ padding: '8px' }}>
                            <span
                              title={o.errorMessage || undefined}
                              style={{
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 700,
                                background:
                                  o.status === 'FILLED' ? '#dcfce7'
                                  : o.status === 'REJECTED' ? '#fee2e2'
                                  : '#f1f5f9',
                                color:
                                  o.status === 'FILLED' ? '#15803d'
                                  : o.status === 'REJECTED' ? '#b91c1c'
                                  : '#64748b',
                              }}
                            >
                              {o.status}
                            </span>
                            {o.status === 'REJECTED' && o.errorMessage && (
                              <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 3, maxWidth: 280 }}>
                                {o.errorMessage}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '8px', color: '#64748b' }}>{fmtDate(o.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Instrument drawer — chart + dollar amount + direct market buy/sell (MI-style) */}
      {selectedId != null && (
        <StockInstrumentDrawer
          open={drawerTicker != null}
          configId={selectedId}
          environment={selectedAccount?.environment || 'DEMO'}
          instrument={drawerInstrument}
          ticker={drawerTicker || ''}
          position={drawerPosition}
          currency={currency}
          canManage={canManage}
          onClose={() => setDrawerTicker(null)}
          onPlaced={() => void loadPortfolio(selectedId)}
        />
      )}

      {/* Buy/Sell modal */}
      {orderModal && selectedId != null && (
        <StockOrderModal
          configId={selectedId}
          currency={currency}
          environment={selectedAccount?.environment || 'DEMO'}
          positions={positions}
          initialSide={orderModal.side}
          initialTicker={orderModal.ticker}
          onClose={() => setOrderModal(null)}
          onPlaced={() => void loadPortfolio(selectedId)}
        />
      )}
    </div>
  );
};

export default StockPortfolio;
